import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import {
  executionStatusLabel,
  executionStatusTone,
  requiredActionLabel,
} from '@/modules/agreements/shared';
import { useMyExecution } from './hooks/use-my-execution';
import { ExecutionEvents } from './parts/execution-events';
import { ExecutionSummary } from './parts/execution-summary';

/**
 * One execution, as the subject sees it: what was signed, when, and the signed PDF.
 *
 * The PDF is the record of the execution. It is generated once, at completion, and is never
 * regenerated — a later change to the document does not alter a signed PDF.
 */
export function ExecutionDetailPage(props: { executionId: string }): ReactNode {
  const { execution, error, isLoading, isDenied } = useMyExecution(props.executionId);

  return (
    <AppDetailLayout
      headerVariant="accent"
      title="Agreement execution"
      subtitle={execution ? execution.executionRef : undefined}
      breadcrumbs={[{ label: 'Agreements', href: '/agreements' }, { label: 'Execution' }]}
      isLoading={isLoading}
      isDenied={isDenied}
      error={error}
      status={
        execution
          ? {
              label: executionStatusLabel(execution.status),
              tone: executionStatusTone(execution.status),
            }
          : undefined
      }
      badges={
        execution
          ? [
              <Badge key="action" tone="info">
                {requiredActionLabel(execution.requiredAction)}
              </Badge>,
            ]
          : undefined
      }
      primaryAction={
        execution?.downloadUrl ? (
          <Button
            onClick={() => {
              window.open(execution.downloadUrl ?? '', '_blank', 'noopener,noreferrer');
            }}
          >
            Download signed PDF
          </Button>
        ) : undefined
      }
      sections={[
        {
          id: 'summary',
          label: 'Summary',
          content: execution ? <ExecutionSummary execution={execution} /> : null,
        },
        {
          id: 'events',
          label: 'Events',
          content: execution ? <ExecutionEvents events={execution.events} /> : null,
        },
      ]}
    />
  );
}
